
import React, { useState, useCallback, useEffect } from 'react';
import { PipelineDetail, getPipelines } from '@/actions/data-fetch-pipelines';
import EnhancedPipelineForm from '../../../components/Forms/EnhancedPipelineForm';
import PipelineListView from './PipelineListView';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { toastMessage } from '@/utils/toast-message';
import { Activity } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface PipelineClientWrapperProps {
  initialData: PipelineDetail[];
  pagination?: PaginationProps;
}

export const PipelineClientWrapper: React.FC<PipelineClientWrapperProps> = ({ 
  initialData, 
  pagination 
}) => {
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineDetail | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState<PipelineDetail[]>(initialData || []);
  const [currentPagination, setCurrentPagination] = useState<PaginationProps | undefined>(pagination);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const fetchData = useCallback(async (params: {
    page?: number;
    search_term?: string;
    sort_column?: string;
    sort_order?: 'ASC' | 'DESC';
  } = {}) => {
    try {
      setIsLoading(true);
      const response = await getPipelines(params);

      if (response.data) {
        setData(response.data.pipelines || []);
        setCurrentPagination(response.data.pagination);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toastMessage(error.message, 'error');
      } else {
        toastMessage('An unknown error occurred', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if we don't have initial data
    if (!initialData || initialData.length === 0) {
      fetchData();
    }
  }, []);

  const handleEdit = (pipeline?: PipelineDetail) => {
    setSelectedPipeline(pipeline || null);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedPipeline(null);
  };

  const handleOpenModal = (pipeline?: PipelineDetail) => {
    setSelectedPipeline(pipeline || null);
    setIsModalOpen(true);
  };

  const handleSuccess = async (updatedPipeline: PipelineDetail) => {
    // Update local state optimistically
    setData(prevData => {
      const index = prevData.findIndex(p => p.uuid === updatedPipeline.uuid);
      if (index !== -1) {
        // Update existing pipeline
        const newData = [...prevData];
        newData[index] = updatedPipeline;
        return newData;
      } else {
        // Add new pipeline
        return [...prevData, updatedPipeline];
      }
    });

    // Close modal
    setIsModalOpen(false);
    setSelectedPipeline(null);

    // Refresh data from server to ensure consistency
    await fetchData({
      page: currentPagination?.currentPage || 1,
      search_term: searchTerm,
      sort_column: sortColumn,
      sort_order: sortOrder
    });

    toastMessage(
      selectedPipeline ? 'Pipeline updated successfully' : 'Pipeline created successfully',
      'success'
    );
  };

  const handlePageChange = async (page: number) => {
    await fetchData({
      page,
      search_term: searchTerm,
      sort_column: sortColumn,
      sort_order: sortOrder
    });
  };

  const handleSearch = async (term: string): Promise<void> => {
    setSearchTerm(term);
    await fetchData({
      page: 1,
      search_term: term,
      sort_column: sortColumn,
      sort_order: sortOrder
    });
  };

  // @ts-expect-error - Reserved for future sorting functionality
  const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
    setSortColumn(column);
    setSortOrder(order);
    await fetchData({
      page: currentPagination?.currentPage || 1,
      search_term: searchTerm,
      sort_column: column,
      sort_order: order
    });
  };

  return (
    <>
      <ModernSettingHeader
        title="Pipeline Management"
        description="Configure your sales pipelines and status workflows. Define the stages that leads will progress through in your sales process."
        buttonText="Create New Pipeline"
        onClick={() => handleOpenModal()}
        icon={<Activity className="w-6 h-6" />}
      />

      <div className="p-6">
        <PipelineListView
          initialData={data}
          onEdit={handleEdit}
          pagination={currentPagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          isLoading={isLoading}
          searchTerm={searchTerm}
        />
      </div>

      {/* Modal/Sidebar Form */}
      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={selectedPipeline ? 'Edit Pipeline' : 'Create New Pipeline'}
        subtitle={selectedPipeline ? 'Modify pipeline stages and workflow configuration' : 'Configure sales stages and status workflows for lead progression'}
        icon={<Activity className="w-6 h-6" />}
      >
        <EnhancedPipelineForm
          onClose={handleClose}
          uuid={selectedPipeline?.uuid}
          initialData={selectedPipeline}
          onSuccess={handleSuccess}
        />
      </SettingsSlideModal>
    </>
  );
};